import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, Badge } from "@/components/ui-kit";
import { JOB_STATUSES, BRANCHES, findMeta, fmtDate, branchColor } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import {
  Activity, Boxes, FolderKanban, AlertTriangle, PackagePlus, ShoppingCart, Flame,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie,
} from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-sm text-muted-foreground">Loading command center…</div>;

  const t = data.totals;
  const kpis = [
    { label: "Active Jobs", value: t.active_jobs, icon: Activity, color: "text-cyan-500", testid: "kpi-active-jobs" },
    { label: "Urgent", value: t.urgent_jobs, icon: Flame, color: "text-red-500", testid: "kpi-urgent" },
    { label: "Active Projects", value: t.active_projects, icon: FolderKanban, color: "text-indigo-500", testid: "kpi-projects" },
    { label: "Low Stock", value: t.low_stock, icon: AlertTriangle, color: "text-amber-500", testid: "kpi-low-stock" },
    { label: "Inventory SKUs", value: t.inventory_items, icon: Boxes, color: "text-emerald-500", testid: "kpi-inventory" },
    { label: "Open POs", value: t.open_pos, icon: ShoppingCart, color: "text-blue-500", testid: "kpi-pos" },
  ];

  const statusData = JOB_STATUSES.map((s) => ({ name: s.label, value: data.status_counts[s.value] || 0 }));
  const branchData = BRANCHES.map((b) => ({ name: b.label, value: data.branch_counts[b.value] || 0, color: b.color }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight sm:text-3xl">Command Center</h1>
          <p className="text-sm text-muted-foreground">Welcome back, {user.full_name.split(" ")[0]}. Live operational snapshot.</p>
        </div>
        <button onClick={() => navigate("/inward?new=1")} data-testid="dash-new-inward"
          className="flex h-9 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground hover:brightness-110">
          <PackagePlus size={16} /> New Inward <kbd className="ml-1 hidden rounded md:inline bg-black/20 px-1 font-mono text-[10px]">Ctrl+N</kbd>
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {kpis.map((k) => (
          <Card key={k.label} className="p-4 transition hover:border-primary/50" data-testid={k.testid}>
            <k.icon size={18} className={k.color} />
            <div className="mt-2 font-mono text-2xl font-bold">{k.value}</div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{k.label}</div>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-4 lg:col-span-2">
          <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wide">Jobs by Status</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={statusData}>
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} angle={-20} textAnchor="end" height={60} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {statusData.map((_, i) => <Cell key={i} fill="hsl(var(--primary))" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-4">
          <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wide">Projects by Branch</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={branchData.filter((b) => b.value > 0)} dataKey="value" nameKey="name" innerRadius={45} outerRadius={85} paddingAngle={3}>
                {branchData.map((b, i) => <Cell key={i} fill={b.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2">
            {branchData.map((b) => (
              <span key={b.name} className="flex items-center gap-1 font-mono text-[10px] uppercase">
                <span className="h-2 w-2 rounded-full" style={{ background: b.color }} /> {b.name} ({b.value})
              </span>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wide">Recent Inward</h3>
          <div className="space-y-1">
            {data.recent_jobs.map((j) => {
              const st = findMeta(JOB_STATUSES, j.status);
              return (
                <button key={j.id} onClick={() => navigate(`/jobs/${j.id}`)}
                  className="flex w-full items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-left text-sm hover:border-primary/50 hover:bg-accent">
                  <div className="min-w-0">
                    <div className="truncate font-semibold">{j.equipment_name}</div>
                    <div className="font-mono text-[11px] text-muted-foreground">{j.job_number} · {j.serial_number}</div>
                  </div>
                  <Badge className={st.cls}>{st.label}</Badge>
                </button>
              );
            })}
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wide">
            <AlertTriangle size={15} className="text-amber-500" /> Low Stock Alerts
          </h3>
          {data.low_stock_items.length === 0 ? (
            <p className="text-sm text-muted-foreground">All critical components above reorder level. ✓</p>
          ) : (
            <div className="space-y-1">
              {data.low_stock_items.map((i) => (
                <div key={i.id} className="flex items-center justify-between rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm">
                  <div>
                    <div className="font-semibold">{i.name}</div>
                    <div className="font-mono text-[11px] text-muted-foreground">{i.part_number} · {i.bin_location}</div>
                  </div>
                  <span className="font-mono text-sm font-bold text-amber-500">{i.quantity_on_hand}/{i.min_reorder_level}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
